"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveTokenLimitMediaTagId = resolveTokenLimitMediaTagId;
exports.resolveTokenLimitMax = resolveTokenLimitMax;
exports.normalizeMediaTypeMaxes = normalizeMediaTypeMaxes;
/** A cap is a positive finite number of hundredths. Anything else means "not set". */
function asCap(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
        ? value
        : null;
}
function mediaTypeMaxesOf(tokenLimitSettings) {
    if (!tokenLimitSettings)
        return null;
    return tokenLimitSettings.mediaTypeMaxes || null;
}
/**
 * The media-type tagId whose cap applies to this tool, or null when none does.
 *
 * No production tool carries two MEDIA tags today, but nothing enforces that:
 * Manage Tool Tags will happily tick both Image and Video on one tool. When more
 * than one cap matches, **the lowest one wins**. This is a restriction, so the
 * tighter of two applicable restrictions applying is the rule a person would
 * expect, and it can be stated in a sentence.
 *
 * The obvious alternative, "first key wins", is not a rule at all: the keys are
 * random Firestore document ids, so it would resolve on whether Image's id
 * happened to sort before Video's. Nobody could predict that, and recreating a
 * tag would flip it.
 *
 * Keys are still sorted so that two caps of EQUAL size name the same tagId
 * everywhere. That only affects which tag is reported, never the number.
 */
function resolveTokenLimitMediaTagId(tokenLimitSettings, nodeDefTags) {
    const mediaTypeMaxes = mediaTypeMaxesOf(tokenLimitSettings);
    if (!mediaTypeMaxes || !nodeDefTags)
        return null;
    let matchedTagId = null;
    let matchedCap = null;
    Object.keys(mediaTypeMaxes).sort().forEach((tagId) => {
        if (!nodeDefTags[tagId])
            return;
        const cap = asCap(mediaTypeMaxes[tagId]);
        if (cap === null)
            return;
        if (matchedCap === null || cap < matchedCap) {
            matchedTagId = tagId;
            matchedCap = cap;
        }
    });
    return matchedTagId;
}
/**
 * The cap that applies to a given tool, in hundredths, or null when the role
 * has no cap to apply.
 *
 * Null is the caller's problem, not this function's: the run paths treat it as
 * a misconfigured role and block, because a TOKEN_LIMIT role with no number is
 * a limit nobody set rather than a limit of infinity.
 */
function resolveTokenLimitMax(tokenLimitSettings, nodeDefTags) {
    const mediaTagId = resolveTokenLimitMediaTagId(tokenLimitSettings, nodeDefTags);
    if (mediaTagId) {
        const mediaTypeMaxes = mediaTypeMaxesOf(tokenLimitSettings);
        return asCap(mediaTypeMaxes ? mediaTypeMaxes[mediaTagId] : null);
    }
    return asCap(tokenLimitSettings ? tokenLimitSettings.max : null);
}
/**
 * The stored shape of a `mediaTypeMaxes` map, with junk dropped: non-positive,
 * non-finite, and non-numeric caps go, because an override that cannot be
 * enforced should not be persisted as though it could.
 *
 * `allowedTagIds`, when given, additionally drops entries for tags that are not
 * MEDIA tags. The role editor passes the live MEDIA tag ids; the callables pass
 * nothing, since they have no tag collection to check against and rejecting an
 * unknown id there would break a role the moment a tag is added.
 */
function normalizeMediaTypeMaxes(mediaTypeMaxes, allowedTagIds) {
    if (!mediaTypeMaxes)
        return {};
    const allowed = allowedTagIds
        ? allowedTagIds.reduce((acc, tagId) => {
            acc[tagId] = true;
            return acc;
        }, {})
        : null;
    return Object.keys(mediaTypeMaxes)
        .sort()
        .reduce((acc, tagId) => {
        if (allowed && !allowed[tagId])
            return acc;
        const cap = asCap(mediaTypeMaxes[tagId]);
        if (cap === null)
            return acc;
        acc[tagId] = cap;
        return acc;
    }, {});
}
